<template>
  <main class="container mx-auto mt-16 py-5 md:pt-12 md:pb-15">
    <div
      class="flex flex-col md:flex-row flex-wrap justify-between mb-8 md:mb-12"
    >
      <h1 class="section-heading mr-4">Articles</h1>
      <div class="filter flex flex-col md:flex-row items-center gap-6">
        <template v-if="applyFiltersLoading">
          <UtilsSkeletonLoading class="w-full md:w-[270px] h-10" />
          <UtilsSkeletonLoading class="w-full md:w-[270px] h-10" />
        </template>
        <template v-else>
          <v-select
            v-model="filterData.industry"
            label="name"
            :options="options.industries"
            :reduce="option => option.name"
            placeholder="Industry"
            class="text-bw-800 w-full md:w-[305px]"
            @update:model-value="onInputIndustry"
          >
          </v-select>
          <v-select
            v-model="filterData.expertise"
            label="name"
            :options="options.expertises"
            :reduce="option => option.name"
            placeholder="Expertise"
            class="text-bw-800 w-full md:w-[305px]"
            @update:model-value="onInputExpertise"
          >
          </v-select>
        </template>
      </div>
    </div>

    <div
      v-if="contentsData.length === 0"
      class="flex flex-col justify-center md:min-h-[35vh]"
    >
      <h2
        class="text-[22px] leading-normal md:text-[40px] md:leading-[50px] font-medium text-bw-900 mb-5 mx-auto text-center"
      >
        We Looked Everywhere
      </h2>
      <p
        class="text-sm sm:text-base text-bw-800 w-[90%] sm:w-3/4 md:w-2/5 mx-auto text-center"
      >
        Sorry, the page you are looking for cannot be found. Please try to enter
        another filters.
      </p>
    </div>

    <div
      v-else
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-12"
    >
      <template v-if="isLoading">
        <div v-for="i in 4" :key="i">
          <UtilsSkeletonLoading class="rounded-lg h-40 w-full mb-4 card-img" />
          <UtilsSkeletonLoading class="w-full h-14 mb-3" />
          <UtilsSkeletonLoading class="w-full h-12" />
        </div>
      </template>

      <template v-else>
        <ArticleCard
          v-for="content in contentsData"
          :key="content.id"
          :with-logo="false"
          :title="content.attributes?.card_title"
          :desc="content.attributes?.layout_component?.[0]?.description"
          :image-url="content.attributes?.image?.data?.attributes?.url"
          :slug="`/ideas/${content.attributes?.slug}`"
        />
        <template v-if="isLoadingMore">
          <div v-for="i in 4" :key="i">
            <UtilsSkeletonLoading
              class="rounded-lg h-40 w-full mb-4 card-img"
            />
            <UtilsSkeletonLoading class="w-full h-14 mb-3" />
            <UtilsSkeletonLoading class="w-full h-12" />
          </div>
        </template>
      </template>
    </div>

    <div
      v-if="contentMeta?.pagination?.page < contentMeta?.pagination?.pageCount"
      class="mx-auto text-center mt-12"
    >
      <CommonLoadMoreButton @click="loadMore" />
    </div>
  </main>
</template>

<script setup>
import { useGlobalStore } from '~/store/global'

const global = useGlobalStore()
const mainSeo = global.mainSeo
const mainBanner = global.mainBanner

const options = ref({
  industries: [],
  expertises: []
})

const route = useRoute()
const router = useRouter()

const isLoading = ref(true)
const isLoadingMore = ref(false)
const applyFiltersLoading = ref(true)
const page = ref(1)

const SEOMeta = ref({})

const contentsData = ref([])
const contentMeta = ref({})

const filterData = ref({
  industry: route.query.industry || null,
  expertise: route.query.expertise || null
})

const articlesQuery = ref({
  'pagination[page]': page,
  'pagination[pageSize]': 16,
  populate:
    'image,layout_component,layout_component.type,expertices,article_industries',
  'filters[$and][0][layout][$eq]': 'article',
  'sort[0]': 'publish_at:desc',
  'sort[1]': 'id:desc'
})
if (route.query.industry) {
  articlesQuery.value['filters[$and][1][article_industries][title][$eq]'] =
    route.query.industry
}
if (route.query.expertise) {
  articlesQuery.value['filters[$and][2][expertices][title][$eq]'] =
    route.query.expertise
}

const [articles, industries, expertises, seo] = await Promise.all([
  useSuitmediaApiData(`/api/contents`, {
    query: { ...articlesQuery.value }
  }),
  useSuitmediaApiData(`/api/industries`, {
    query: {
      'pagination[pageSize]': 50,
      'fields[0]': 'id',
      'fields[1]': 'title',
      'sort[0]': 'title:asc'
    }
  }),
  useSuitmediaApiData(`/api/expertices`, {
    query: {
      'pagination[pageSize]': 50,
      'fields[0]': 'id',
      'fields[1]': 'title',
      'sort[0]': 'title:asc'
    }
  }),
  useSuitmediaApiData(`/api/seo-metas`, {
    query: {
      'filters[url]': route.path,
      populate: 'seo.image'
    }
  })
])

isLoading.value =
  articles.pending.value || industries.pending.value || expertises.pending.value
applyFiltersLoading.value = industries.pending.value || expertises.pending.value

if (articles.error.value) {
  throw createError({
    statusCode: 404,
    message: `Articles does not exist`
  })
}

SEOMeta.value = seo.data?.value?.data?.[0]

contentsData.value = articles.data.value.data
contentMeta.value = articles.data.value.meta

options.value.expertises = expertises.data.value.data.map(item => {
  return {
    id: item.id,
    name: item.attributes.title
  }
})

options.value.industries = industries.data.value.data.map(item => {
  return {
    id: item.id,
    name: item.attributes.title
  }
})

const loadMore = async e => {
  e.currentTarget.blur()
  isLoadingMore.value = true
  page.value++
  await getContents(true)
  isLoadingMore.value = false
  global.changeLoadedMore()
}

const getContents = async isLoadMore => {
  const query = {
    'pagination[page]': page,
    'pagination[pageSize]': 16,
    populate:
      'image,layout_component,layout_component.type,expertices,article_industries',
    'filters[$and][0][layout][$eq]': 'article',
    'sort[0]': 'publish_at:desc',
    'sort[1]': 'id:desc'
  }

  if (filterData.value.industry) {
    query['filters[$and][1][article_industries][title][$eq]'] =
      filterData.value.industry
  }
  if (filterData.value.expertise) {
    query['filters[$and][2][expertices][title][$eq]'] =
      filterData.value.expertise
  }

  const { data, error, pending } = await useSuitmediaApiData(`/api/contents`, {
    query
  })

  isLoading.value = pending.value
  applyFiltersLoading.value = pending.value

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: `Articles does not exist`
    })
  }

  if (isLoadMore) {
    contentsData.value = [...contentsData.value, ...data.value.data]
    contentMeta.value = data.value.meta
  } else {
    contentsData.value = data.value.data
    contentMeta.value = data.value.meta
  }
}

const onInputExpertise = async expertise => {
  isLoading.value = true
  page.value = 1

  filterData.value.industry = null
  const currentQuery = route.query
  delete currentQuery.expertise

  router.push({ query: { expertise } })
  await getContents()
}
const onInputIndustry = async val => {
  isLoading.value = true
  page.value = 1

  filterData.value.expertise = null
  const currentQuery = route.query
  delete currentQuery.industry

  router.push({ query: { industry: val } })
  await getContents()
}

useJsonld({
  '@context': 'https://schema.org/',
  '@type': 'Organization',
  name: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  url: SEOMeta?.value?.attributes?.url || 'suitmedia.com',
  description:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  image:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png'
})

useSeoMeta({
  title: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  ogTitle: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  description:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogDescription:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogImage:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png',
  ogType: SEOMeta?.value?.attributes?.seo?.open_graph_type,
  twitterTitle: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  twitterDescription:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  twitterImage:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png'
})
</script>

<style lang="scss" scoped>
.filter {
  :deep(.vs--open) {
    .vs__dropdown-toggle {
      padding: 10px;

      background: linear-gradient(#ff6700, #ff6700),
        linear-gradient(#ff6700, #ff6700), linear-gradient(#ff6700, #ff6700);
      background-size:
        1px 20%,
        100% 1px,
        1px 20%;
      background-position:
        bottom left,
        bottom center,
        bottom right;
      background-repeat: no-repeat;

      border: none;
    }
  }
  :deep(.vs__dropdown-toggle) {
    padding: 10px;

    background: linear-gradient(#a5a5a5, #a5a5a5),
      linear-gradient(#a5a5a5, #a5a5a5), linear-gradient(#a5a5a5, #a5a5a5);
    background-size:
      1px 20%,
      100% 1px,
      1px 20%;
    background-position:
      bottom left,
      bottom center,
      bottom right;
    background-repeat: no-repeat;

    border: none;
    border-radius: 0;
    // color: #999;
  }
  :deep(.vs__selected-options) {
    flex-wrap: nowrap;
  }
}
</style>
